import React, { useState, useEffect } from "react";
import { Settings, Zap, Plus, Plane, Trash2, X, Server, Check, AlertTriangle, Eye, Lock, KeyRound, History } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { StaffAccessPanel } from "./StaffAccessPanel";
import { useFinancialPrivacy } from "../../lib/financialPrivacy";
import {
  COUNTRIES, LANGUAGES, ADDRESSING_MODES, CURRENCIES,
  TIME_FORMATS, DATE_FORMATS, TIMEZONES, findCountry,
} from "../../lib/i18nCatalog";

/** Full Settings tab — sub-tabs for Fare / Surge / Cycle / Fixed Routes / Diagnostics. */
export const SettingsPanel = ({ settingsTab, setSettingsTab, fareConfig, surgeConfig, cycleConfig, fixedRoutes, onUpdateFare, onUpdateSurge, onUpdateCycle, onSaveRoute, onDeleteRoute }) => (
  <div className="bg-white" data-testid="settings-panel">
    <div className="px-4 py-3 border-b border-border flex gap-1 overflow-x-auto">
      <button
        onClick={() => setSettingsTab("fare")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "fare" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-fare"
      >
        Fare Rules
      </button>
      <button
        onClick={() => setSettingsTab("surge")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "surge" ? "bg-[#FFC107] text-black border-[#FFC107]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-surge"
      >
        Surge Pricing
      </button>
      <button
        onClick={() => setSettingsTab("cycle")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "cycle" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-cycle"
      >
        Cycle Fee
      </button>
      <button
        onClick={() => setSettingsTab("routes")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "routes" ? "bg-[#10B981] text-white border-[#10B981]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-routes"
      >
        Fixed Routes
      </button>
      <button
        onClick={() => setSettingsTab("memberships")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "memberships" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-memberships"
      >
        Memberships
      </button>
      <button
        onClick={() => setSettingsTab("staff")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "staff" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-staff"
      >
        Staff &amp; Access
      </button>
      <button
        onClick={() => setSettingsTab("region")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "region" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-region"
      >
        Region &amp; Language
      </button>
      <button
        onClick={() => setSettingsTab("promotion")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "promotion" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-promotion"
      >
        Scheduling
      </button>
      <button
        onClick={() => setSettingsTab("privacy")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "privacy" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-privacy"
      >
        Privacy
      </button>
      <button
        onClick={() => setSettingsTab("diagnostics")}
        className={`flex-1 py-2 label-mini transition-colors border whitespace-nowrap px-3 ${settingsTab === "diagnostics" ? "bg-[#6B7280] text-white border-[#6B7280]" : "border-border hover:bg-secondary"}`}
        data-testid="settings-tab-diagnostics"
      >
        Diagnostics
      </button>
    </div>
    <div className="p-4 max-w-3xl mx-auto">
      {settingsTab === "fare" && <FareConfigForm fareConfig={fareConfig} onSave={onUpdateFare} />}
      {settingsTab === "surge" && <SurgeConfigForm surgeConfig={surgeConfig} onSave={onUpdateSurge} />}
      {settingsTab === "cycle" && <CycleConfigForm cycleConfig={cycleConfig} onSave={onUpdateCycle} />}
      {settingsTab === "routes" && <FixedRoutesPanel routes={fixedRoutes} onSave={onSaveRoute} onDelete={onDeleteRoute} />}
      {settingsTab === "memberships" && <MembershipGateForm />}
      {settingsTab === "staff" && <StaffAccessPanel />}
      {settingsTab === "region" && <RegionLanguageForm />}
      {settingsTab === "promotion" && <SchedulingPromotionForm />}
      {settingsTab === "privacy" && <PrivacySettingsForm />}
      {settingsTab === "diagnostics" && <DiagnosticsCard />}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
 *  DIAGNOSTICS — shows the SANITIZED runtime config from /admin/runtime-config
 *  so operators can confirm which DB / commit / TTL the backend is on. No
 *  credentials are ever surfaced.
 * ──────────────────────────────────────────────────────────────────────── */

const prettyDbKind = (k) => {
  if (k === "local") return "Local MongoDB (in-container)";
  if (k === "atlas") return "MongoDB Atlas";
  if (k === "other") return "Other (managed/self-hosted)";
  return k || "unknown";
};

/** Drift banner — turns RED when the live runtime disagrees with the
 *  operator's `EXPECTED_DB_NAME` / `EXPECTED_MONGO_HOST` env vars, AMBER
 *  when those vars are unset (so the operator notices they should configure
 *  them), and stays hidden when everything matches. */
const DriftBanner = ({ drift, dbKind }) => {
  if (!drift) return null;

  if (drift.status === "mismatch") {
    return (
      <div
        className="border-2 border-destructive bg-destructive/10 p-3"
        data-testid="drift-banner"
        data-drift-status="mismatch"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <div className="font-heading font-black text-destructive">
            CONFIG DRIFT DETECTED
          </div>
        </div>
        <p className="text-sm text-destructive mb-2">
          The live backend is running on{" "}
          <span className="font-bold">{prettyDbKind(dbKind)}</span>, but the
          expected values you configured don't match. Manual bookings are
          being written to a database different from the one you're
          inspecting.
        </p>
        <ul className="text-xs font-mono-tab space-y-1">
          {drift.mismatches.map((m) => (
            <li
              key={m.field}
              className="border border-destructive/40 bg-white px-2 py-1"
              data-testid={`drift-mismatch-${m.field}`}
            >
              <span className="font-bold">{m.field}</span>
              {": "}expected{" "}
              <span className="text-destructive">{String(m.expected)}</span>
              {" — actual "}
              <span className="text-destructive">{String(m.actual)}</span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted-foreground mt-2">
          Fix: redeploy the expected version, OR update the relevant{" "}
          <code>EXPECTED_*</code> values in <code>backend/.env</code> to
          match what should be running, then restart the backend (
          <code>sudo supervisorctl restart backend</code>).
        </p>
      </div>
    );
  }

  if (drift.status === "unconfigured") {
    return (
      <div
        className="border-2 border-[#F59E0B] bg-[#FFFBEB] p-3"
        data-testid="drift-banner"
        data-drift-status="unconfigured"
      >
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
          <div className="font-heading font-black text-[#92400E]">
            DRIFT DETECTION UNCONFIGURED
          </div>
        </div>
        <p className="text-sm text-[#92400E]">
          This backend is currently running on{" "}
          <span className="font-bold">{prettyDbKind(dbKind)}</span>. Set{" "}
          <code className="font-mono-tab">EXPECTED_DB_NAME</code>,{" "}
          <code className="font-mono-tab">EXPECTED_MONGO_HOST</code>, and/or{" "}
          <code className="font-mono-tab">EXPECTED_GIT_COMMIT</code> in{" "}
          <code className="font-mono-tab">backend/.env</code> to enable
          automatic drift alerts — the banner will turn red the moment the
          live backend drifts from what you expect.
        </p>
      </div>
    );
  }

  // status === "ok"
  const parts = [
    drift.expected_db_name ? `DB ${drift.expected_db_name}` : null,
    drift.expected_mongo_host ? `host ${drift.expected_mongo_host}` : null,
    drift.expected_git_commit ? `commit ${drift.expected_git_commit}` : null,
  ].filter(Boolean).join(", ");
  return (
    <div
      className="border-2 border-[#10B981] bg-[#ECFDF5] p-3 flex items-center gap-2"
      data-testid="drift-banner"
      data-drift-status="ok"
    >
      <Check className="w-5 h-5 text-[#10B981]" />
      <div>
        <div className="font-heading font-black text-[#065F46]">
          CONFIG IN SYNC
        </div>
        <p className="text-sm text-[#065F46]">
          Live backend matches expected {parts}. Running on{" "}
          <span className="font-bold">{prettyDbKind(dbKind)}</span>.
        </p>
      </div>
    </div>
  );
};

const DiagnosticsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchCfg = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/runtime-config");
      setCfg(data);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCfg(); }, []);

  const rows = cfg ? [
    { label: "App Environment", value: cfg.app_env, testid: "diag-app-env" },
    { label: "Database Kind", value: prettyDbKind(cfg.db_kind), testid: "diag-db-kind",
      tone: cfg.db_kind === "atlas" ? "ok" : cfg.db_kind === "local" ? "warn" : null },
    { label: "Database Name", value: cfg.db_name, testid: "diag-db-name" },
    { label: "Mongo Host", value: cfg.mongo_host, testid: "diag-mongo-host", mono: true },
    { label: "DB Connectivity", value: cfg.db_ping_ok ? "OK" : "FAILED", testid: "diag-db-ping",
      tone: cfg.db_ping_ok ? "ok" : "err" },
    { label: "Git Commit", value: cfg.git_commit, testid: "diag-git-commit", mono: true },
    { label: "Last Commit Message", value: cfg.git_message || "—", testid: "diag-git-message" },
    { label: "Offer TTL", value: `${cfg.offer_ttl_seconds}s`, testid: "diag-offer-ttl" },
    { label: "Lifecycle Path", value: cfg.lifecycle_path, testid: "diag-lifecycle-path",
      tone: cfg.lifecycle_path === "transition_ride" ? "ok" : "warn" },
    { label: "Server Time", value: cfg.server_time, testid: "diag-server-time", mono: true },
  ] : [];

  return (
    <div className="space-y-4" data-testid="diagnostics-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-muted-foreground" />
          <div>
            <h3 className="font-heading text-lg font-black">Runtime Config</h3>
            <p className="label-mini text-muted-foreground">
              Sanitized snapshot of the live backend. Useful when comparing live behavior against a DB you're inspecting in Atlas/Compass.
            </p>
          </div>
        </div>
        <button
          onClick={fetchCfg}
          disabled={loading}
          className="text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors disabled:opacity-60"
          data-testid="diag-refresh-btn"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="border-2 border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="diag-error">
          {err}
        </div>
      )}

      {cfg && <DriftBanner drift={cfg.drift} dbKind={cfg.db_kind} />}

      {cfg && (
        <div className="border border-border divide-y divide-border" data-testid="diag-table">
          {rows.map((r) => (
            <div key={r.testid} className="flex items-center justify-between px-3 py-2" data-testid={r.testid}>
              <div className="label-mini text-muted-foreground">{r.label}</div>
              <div className={`text-sm font-semibold flex items-center gap-1.5 ${r.mono ? "font-mono-tab" : ""}`}>
                {r.tone === "ok" && <Check className="w-3.5 h-3.5 text-[#10B981]" />}
                {r.tone === "err" && <AlertTriangle className="w-3.5 h-3.5 text-destructive" />}
                {r.tone === "warn" && <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" />}
                <span className={r.tone === "err" ? "text-destructive" : ""}>{r.value}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground leading-relaxed">
        <strong>What to check when live behavior disagrees with Atlas/Compass:</strong>
        <ol className="list-decimal pl-4 mt-1 space-y-0.5">
          <li><span className="font-mono-tab">Database Name</span> matches the DB you're inspecting.</li>
          <li><span className="font-mono-tab">Mongo Host</span> is the cluster you expect (Atlas vs local).</li>
          <li><span className="font-mono-tab">Git Commit</span> matches the deploy you intended.</li>
          <li><span className="font-mono-tab">Lifecycle Path = transition_ride</span> (canonical state machine, not the deprecated dual_write).</li>
        </ol>
      </div>
    </div>
  );
};


const MembershipGateForm = () => {
  const [required, setRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/operator-settings");
        setRequired(data?.settings?.subscription_required === true);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put("/admin/operator-settings", { subscription_required: next });
      setRequired(next);
      toast.success(next ? "Subscription is now REQUIRED to go online" : "Subscription requirement turned off");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="membership-gate-form">
      <div>
        <div className="label-mini text-muted-foreground">DRIVER MEMBERSHIPS</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Subscription access gate</h3>
        <p className="text-xs text-muted-foreground mt-1">
          When ON, a driver must hold an <span className="font-semibold">active membership</span> to go
          online. Drivers without one are blocked at the GO ONLINE step with a prompt to subscribe. This
          gate runs only on the online toggle — it never affects active trips, dispatch, or job allocation.
        </p>
      </div>

      <div className="flex items-center justify-between border border-border p-3">
        <div>
          <div className="label-mini">REQUIRE SUBSCRIPTION TO GO ONLINE</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {required ? "ON — drivers need an active membership" : "OFF — any approved driver can go online"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => save(!required)}
          disabled={saving}
          className={`w-12 h-6 rounded-full transition-colors relative disabled:opacity-60 ${required ? "bg-[#10B981]" : "bg-muted"}`}
          data-testid="subscription-required-toggle"
          aria-pressed={required}
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${required ? "translate-x-6" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div className="text-xs text-muted-foreground border-l-2 border-[#F59E0B] bg-[#FFFBEB] p-3">
        <strong>Before turning this on:</strong> make sure at least one membership plan is active
        (Subscriptions tab) and your drivers have had a chance to subscribe — otherwise they'll be locked
        out of going online.
      </div>
    </div>
  );
};


const REGION_INPUT_CLS = "mt-1.5 w-full border border-border bg-white px-3 py-2 text-sm";

/** Region & Language — read/write the SAME operator_settings i18n fields the
 *  Setup wizard's Operating-area step writes. Reuses GET/PUT /admin/operator-
 *  settings (no new backend) and the shared i18nCatalog. Config-only: does not
 *  touch dispatch / lifecycle / offer routing / allocation / Voice AI. */
const RegionLanguageForm = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/operator-settings");
        const s = data?.settings || {};
        setForm({
          country: s.country || "",
          currency: s.currency || "",
          timezone: s.timezone || "",
          date_format: s.date_format || "",
          time_format: s.time_format || "",
          addressing_mode: s.addressing_mode || "",
          default_language: s.default_language || "",
          supported_languages: Array.isArray(s.supported_languages) ? s.supported_languages : [],
        });
      } catch (e) {
        toast.error(formatApiError(e));
        setForm({ country: "", currency: "", timezone: "", date_format: "", time_format: "", addressing_mode: "", default_language: "", supported_languages: [] });
      }
    })();
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const applyCountry = (code) => {
    const c = findCountry(code);
    setForm((s) => ({
      ...s,
      country: code,
      ...(c ? { currency: c.currency, timezone: c.timezone, date_format: c.dateFormat, time_format: c.timeFormat, addressing_mode: c.addressingMode } : {}),
    }));
  };

  const toggleLang = (code) => setForm((s) => {
    const langs = Array.isArray(s.supported_languages) ? s.supported_languages : [];
    return { ...s, supported_languages: langs.includes(code) ? langs.filter((x) => x !== code) : [...langs, code] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/operator-settings", {
        country: form.country || null,
        currency: form.currency || null,
        timezone: form.timezone || null,
        date_format: form.date_format || null,
        time_format: form.time_format || null,
        addressing_mode: form.addressing_mode || null,
        default_language: form.default_language || null,
        supported_languages: form.supported_languages,
      });
      toast.success("Region & language saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const langs = form.supported_languages || [];
  const REQUIRED = [
    ["country", "Country"],
    ["currency", "Currency"],
    ["timezone", "Time zone"],
    ["addressing_mode", "Addressing mode"],
    ["default_language", "Default language"],
  ];
  const missing = REQUIRED.filter(([k]) => !form[k]).map(([, label]) => label);
  if (!langs.length) missing.push("At least one supported language");
  const canSave = missing.length === 0;

  return (
    <div className="space-y-5" data-testid="region-language-form">
      <div>
        <div className="label-mini text-muted-foreground">REGION &amp; LANGUAGE</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Country, currency &amp; languages</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Tailors addressing, currency, dates and customer languages to where you operate. Picking a country
          sets sensible defaults you can still edit. Config-only — this never affects dispatch, pricing or the
          Voice AI booking flow.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="label-mini">COUNTRY</Label>
          <select className={REGION_INPUT_CLS} value={form.country || ""} onChange={(e) => applyCountry(e.target.value)} data-testid="region-select-country">
            <option value="">Select…</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-mini">CURRENCY</Label>
          <select className={REGION_INPUT_CLS} value={form.currency || ""} onChange={(e) => set("currency", e.target.value)} data-testid="region-select-currency">
            <option value="">Select…</option>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-mini">TIME ZONE</Label>
          <select className={REGION_INPUT_CLS} value={form.timezone || ""} onChange={(e) => set("timezone", e.target.value)} data-testid="region-select-timezone">
            <option value="">Select…</option>
            {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-mini">DATE FORMAT</Label>
          <select className={REGION_INPUT_CLS} value={form.date_format || ""} onChange={(e) => set("date_format", e.target.value)} data-testid="region-select-date-format">
            <option value="">Select…</option>
            {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-mini">TIME FORMAT</Label>
          <select className={REGION_INPUT_CLS} value={form.time_format || ""} onChange={(e) => set("time_format", e.target.value)} data-testid="region-select-time-format">
            <option value="">Select…</option>
            {TIME_FORMATS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-mini">ADDRESSING MODE</Label>
          <select className={REGION_INPUT_CLS} value={form.addressing_mode || ""} onChange={(e) => set("addressing_mode", e.target.value)} data-testid="region-select-addressing-mode">
            <option value="">Select…</option>
            {ADDRESSING_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>
      {form.addressing_mode && (
        <div className="text-[11px] text-muted-foreground" data-testid="region-addressing-mode-hint">
          {ADDRESSING_MODES.find((m) => m.value === form.addressing_mode)?.hint}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="label-mini">DEFAULT LANGUAGE</Label>
          <select className={REGION_INPUT_CLS} value={form.default_language || ""} onChange={(e) => set("default_language", e.target.value)} data-testid="region-select-default-language">
            <option value="">Select…</option>
            {LANGUAGES.filter((l) => l.enabled).map((l) => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label className="label-mini text-muted-foreground mb-1 block">SUPPORTED CUSTOMER LANGUAGES</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {LANGUAGES.map((l) => (
            <label key={l.code} className={`flex items-center gap-2 text-sm ${l.enabled ? "cursor-pointer" : "opacity-60"}`}>
              <input type="checkbox" checked={langs.includes(l.code)} onChange={() => toggleLang(l.code)} data-testid={`region-lang-${l.code}`} disabled={!l.enabled} />
              <span>{l.flag} {l.name}{!l.enabled && " · soon"}</span>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1.5">
          The AI Booking assistant (Website, Passenger app &amp; Admin draft) now auto-detects and replies in
          any language you enable here, defaulting to your chosen default language. On the phone line the AI
          detects Shona/Ndebele and hands the caller to a colleague (voice can't yet speak those languages).
        </div>
      </div>

      {!canSave && (
        <div className="text-xs border-l-2 border-[#F59E0B] bg-[#FFFBEB] text-[#92400E] p-3" data-testid="region-missing-hint">
          Before saving, please complete: <span className="font-semibold">{missing.join(", ")}</span>.
        </div>
      )}

      <Button onClick={save} disabled={saving || !canSave} className="w-full" data-testid="region-save-btn">
        <Settings className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save region & language"}
      </Button>
    </div>
  );
};


const PROMO_DEFAULTS = {
  dynamic_promotion_enabled: true,
  promotion_buffer_min: 10,
  promotion_min_lead_min: 15,
  promotion_max_lead_min: 120,
  promotion_fallback_speed_kmh: 60,
  promotion_road_factor: 1.3,
};

const PROMO_INPUT_CLS = "mt-1.5 w-full border border-border bg-white px-3 py-2 text-sm";

const PromoNumField = ({ testid, label, hint, step = "1", value, disabled, onChange }) => (
  <div>
    <Label className="label-mini">{label}</Label>
    <Input type="number" step={step} className={PROMO_INPUT_CLS} value={value ?? ""}
      onChange={onChange} data-testid={testid} disabled={disabled} />
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </div>
);

/** ETA-aware scheduled promotion settings. Reuses GET/PUT /admin/operator-
 *  settings (no new backend). Config-only: controls WHEN a scheduled ride is
 *  released to drivers — never touches dispatch/allocation/lifecycle. */
const SchedulingPromotionForm = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/operator-settings");
        const s = data?.settings || {};
        setForm({
          dynamic_promotion_enabled: s.dynamic_promotion_enabled ?? PROMO_DEFAULTS.dynamic_promotion_enabled,
          promotion_buffer_min: s.promotion_buffer_min ?? PROMO_DEFAULTS.promotion_buffer_min,
          promotion_min_lead_min: s.promotion_min_lead_min ?? PROMO_DEFAULTS.promotion_min_lead_min,
          promotion_max_lead_min: s.promotion_max_lead_min ?? PROMO_DEFAULTS.promotion_max_lead_min,
          promotion_fallback_speed_kmh: s.promotion_fallback_speed_kmh ?? PROMO_DEFAULTS.promotion_fallback_speed_kmh,
          promotion_road_factor: s.promotion_road_factor ?? PROMO_DEFAULTS.promotion_road_factor,
          has_base: s.base_lat != null && s.base_lng != null,
        });
      } catch (e) {
        toast.error(formatApiError(e));
        setForm({ ...PROMO_DEFAULTS, has_base: false });
      }
    })();
  }, []);

  const setNum = (k, v) => setForm((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));

  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const num = (k) => (form[k] === "" || form[k] == null ? NaN : Number(form[k]));
  const ranges = {
    promotion_buffer_min: [0, 180],
    promotion_min_lead_min: [1, 240],
    promotion_max_lead_min: [1, 1440],
    promotion_fallback_speed_kmh: [5, 200],
    promotion_road_factor: [1, 3],
  };
  const problems = [];
  Object.entries(ranges).forEach(([k, [lo, hi]]) => {
    const v = num(k);
    if (Number.isNaN(v)) problems.push(`${k.replace(/_/g, " ")} is required`);
    else if (v < lo || v > hi) problems.push(`${k.replace(/_/g, " ")} must be ${lo}–${hi}`);
  });
  if (!Number.isNaN(num("promotion_min_lead_min")) && !Number.isNaN(num("promotion_max_lead_min"))
      && num("promotion_min_lead_min") > num("promotion_max_lead_min")) {
    problems.push("Min lead cannot exceed max lead");
  }
  const canSave = problems.length === 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/operator-settings", {
        dynamic_promotion_enabled: !!form.dynamic_promotion_enabled,
        promotion_buffer_min: num("promotion_buffer_min"),
        promotion_min_lead_min: num("promotion_min_lead_min"),
        promotion_max_lead_min: num("promotion_max_lead_min"),
        promotion_fallback_speed_kmh: num("promotion_fallback_speed_kmh"),
        promotion_road_factor: num("promotion_road_factor"),
      });
      toast.success("Scheduling promotion settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const numField = (k, label, hint, step = "1") => (
    <PromoNumField testid={`promo-${k}`} label={label} hint={hint} step={step}
      value={form[k]} disabled={!form.dynamic_promotion_enabled}
      onChange={(e) => setNum(k, e.target.value)} />
  );

  return (
    <div className="space-y-5" data-testid="scheduling-promotion-form">
      <div>
        <div className="label-mini text-muted-foreground">SCHEDULING</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">ETA-aware ride release</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Long-distance pickups are released to drivers <span className="font-semibold">earlier</span>, based on
          the estimated drive time from your base to the pickup — so a driver can physically arrive on time.
          Release time = <span className="font-mono">pickup time − (ETA to pickup + buffer)</span>, kept within
          your min/max limits. Local jobs still release around the minimum lead. Config-only: this never changes
          how rides are allocated or dispatched.
        </p>
      </div>

      {!form.has_base && (
        <div className="flex items-start gap-3 border border-[#FCD34D] border-l-4 border-l-[#F59E0B] bg-[#FFFBEB] text-[#92400E] rounded-md p-3.5" data-testid="promo-no-base-warning" role="alert">
          <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-bold" data-testid="promo-no-base-warning-title">
              Operator base location is not set — ETA-aware scheduled promotion is using the fixed 15-minute fallback.
            </p>
            <p className="text-xs text-[#92400E]/90">
              Set your dispatch base at <span className="font-semibold">Admin → Setup → Operating Area → Centre Latitude / Centre Longitude</span>.
              Once saved, new scheduled rides release based on the estimated drive time to the pickup (ETA + buffer) instead of a fixed 15 minutes.
            </p>
          </div>
        </div>
      )}

      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!form.dynamic_promotion_enabled}
          onChange={(e) => setForm((s) => ({ ...s, dynamic_promotion_enabled: e.target.checked }))}
          data-testid="promo-dynamic_promotion_enabled" />
        <span className="text-sm font-medium">Enable ETA-aware promotion</span>
        <span className="text-[11px] text-muted-foreground">(off = fixed minimum-lead release for every ride)</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {numField("promotion_buffer_min", "OPERATOR BUFFER (MIN)", "Extra minutes added on top of the ETA (e.g. driver prep / parking).")}
        {numField("promotion_min_lead_min", "MIN LEAD (MIN)", "Never release later than this before pickup (local jobs).")}
        {numField("promotion_max_lead_min", "MAX LEAD (MIN)", "Safety ceiling — never release earlier than this before pickup.")}
        {numField("promotion_fallback_speed_kmh", "FALLBACK SPEED (KM/H)", "Used to estimate ETA when live routing is unavailable.")}
        {numField("promotion_road_factor", "ROAD FACTOR", "Multiplier on straight-line distance for the fallback estimate (≈1.3).", "0.1")}
      </div>

      {!canSave && (
        <div className="text-xs border-l-2 border-[#EF4444] bg-[#FEF2F2] text-[#991B1B] p-3" data-testid="promo-validation-hint">
          Please fix: {problems.join("; ")}.
        </div>
      )}

      <Button onClick={save} disabled={saving || !canSave} className="w-full" data-testid="promo-save-btn">
        <Settings className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save scheduling settings"}
      </Button>
    </div>
  );
};


const AUDIT_LABELS = {
  revealed: { label: "Revealed", tone: "text-[#16A34A]" },
  hidden: { label: "Hidden", tone: "text-[#6B7280]" },
  auto_relock: { label: "Auto re-lock", tone: "text-[#6B7280]" },
  unlock_success: { label: "Auth success", tone: "text-[#16A34A]" },
  unlock_failed: { label: "Auth failed", tone: "text-[#EF4444]" },
  audit_enabled: { label: "Audit enabled", tone: "text-[#002FA7]" },
};

const fmtAuditTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const MODE_OPTIONS = [
  { id: "protected", title: "Hide financials by default on login", recommended: true, desc: "Recommended. Gross/Refunds/Net load hidden on every login; click Reveal Financials and confirm with your PIN (or admin password if no PIN is set). Auto-relocks after 5 minutes of inactivity.", icon: Lock },
  { id: "visible", title: "Always show financials after login", desc: "Figures are always visible. No reveal step.", icon: Eye },
];

const PrivacySettingsForm = () => {
  const { refresh } = useFinancialPrivacy();
  const [cfg, setCfg] = useState(null);
  const [savingMode, setSavingMode] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  // PIN form
  const [pinPassword, setPinPassword] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  // Clear PIN form
  const [clearPassword, setClearPassword] = useState("");
  const [clearBusy, setClearBusy] = useState(false);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const { data } = await api.get("/admin/privacy/audit", { params: { limit: 50 } });
      setEvents(data.events || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEventsLoading(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/privacy/config");
      setCfg(data);
      if (data.audit_enabled) loadEvents();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleAudit = async (next) => {
    setAuditBusy(true);
    try {
      await api.put("/admin/privacy/config", { audit_enabled: next });
      setCfg((c) => ({ ...c, audit_enabled: next }));
      await refresh();
      toast.success(next ? "Financial Access Audit enabled" : "Financial Access Audit disabled");
      if (next) loadEvents();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAuditBusy(false); }
  };

  const saveMode = async (mode) => {
    setSavingMode(true);
    try {
      await api.put("/admin/privacy/config", { mode });
      setCfg((c) => ({ ...c, mode }));
      await refresh();
      toast.success("Privacy mode updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingMode(false); }
  };

  const savePin = async () => {
    if (!/^\d{4,8}$/.test(newPin)) { toast.error("PIN must be 4–8 digits"); return; }
    if (newPin !== confirmPin) { toast.error("PINs do not match"); return; }
    setPinBusy(true);
    try {
      await api.post("/admin/privacy/pin", { password: pinPassword, new_pin: newPin });
      setPinPassword(""); setNewPin(""); setConfirmPin("");
      await load(); await refresh();
      toast.success("Financial PIN saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPinBusy(false); }
  };

  const clearPin = async () => {
    setClearBusy(true);
    try {
      await api.delete("/admin/privacy/pin", { data: { password: clearPassword } });
      setClearPassword("");
      await load(); await refresh();
      toast.success("Financial PIN removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClearBusy(false); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="privacy-settings-form">
      <div>
        <div className="label-mini text-muted-foreground">FINANCIAL PRIVACY</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Protect financial figures</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Controls whether revenue, refunds and other money figures are visible on the dashboard.
          Display-only — this never affects pricing, payments, reporting or dispatch.
        </p>
      </div>

      {/* Mode selector */}
      <div className="space-y-2">
        {MODE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = cfg.mode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={savingMode}
              onClick={() => saveMode(opt.id)}
              className={`w-full text-left border p-3 rounded-lg flex items-start gap-3 transition-colors disabled:opacity-60 ${active ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-border hover:bg-secondary"}`}
              data-testid={`privacy-mode-${opt.id}`}
            >
              <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? "bg-[#002FA7] text-white" : "bg-secondary text-muted-foreground"}`}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2">
                  {opt.title}
                  {opt.recommended && <span className="text-[10px] font-bold uppercase tracking-wide text-[#16A34A] bg-[#DCFCE7] px-1.5 py-0.5 rounded" data-testid="privacy-mode-recommended">Recommended</span>}
                  {active && <Check className="w-3.5 h-3.5 text-[#002FA7]" />}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* PIN management */}
      <div className="border-t border-border pt-5 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="label-mini">YOUR REVEAL PIN</div>
            <div className="text-xs text-muted-foreground">
              {cfg.has_pin
                ? "A PIN is set for your account. You'll use it to reveal figures in protected mode."
                : "No PIN set. In protected mode you'll reveal figures with your admin password until you set a PIN."}
            </div>
          </div>
        </div>

        <div className="grid gap-3 max-w-md">
          <div>
            <Label className="label-mini">ADMIN PASSWORD</Label>
            <Input type="password" value={pinPassword} onChange={(e) => setPinPassword(e.target.value)}
              placeholder="Confirm it's you" className="mt-1.5" data-testid="privacy-pin-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-mini">{cfg.has_pin ? "NEW PIN" : "PIN"} (4–8 digits)</Label>
              <Input type="password" inputMode="numeric" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                maxLength={8} className="mt-1.5 font-mono-tab" data-testid="privacy-pin-new" />
            </div>
            <div>
              <Label className="label-mini">CONFIRM PIN</Label>
              <Input type="password" inputMode="numeric" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                maxLength={8} className="mt-1.5 font-mono-tab" data-testid="privacy-pin-confirm" />
            </div>
          </div>
          <Button onClick={savePin} disabled={pinBusy || !pinPassword || !newPin} data-testid="privacy-pin-save">
            <KeyRound className="w-4 h-4 mr-2" /> {cfg.has_pin ? "Update PIN" : "Set PIN"}
          </Button>
        </div>

        {cfg.has_pin && (
          <div className="grid gap-2 max-w-md border-l-2 border-[#EF4444] bg-[#FEF2F2] p-3 rounded-r-lg">
            <div className="label-mini text-[#991B1B]">REMOVE PIN</div>
            <div className="flex gap-2">
              <Input type="password" value={clearPassword} onChange={(e) => setClearPassword(e.target.value)}
                placeholder="Admin password" data-testid="privacy-pin-clear-password" />
              <Button variant="outline" onClick={clearPin} disabled={clearBusy || !clearPassword}
                className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white whitespace-nowrap"
                data-testid="privacy-pin-clear">
                Remove
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Financial Access Audit */}
      <div className="border-t border-border pt-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <History className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div>
              <div className="label-mini">FINANCIAL ACCESS AUDIT</div>
              <div className="text-xs text-muted-foreground">
                Optional, append-only log of who revealed/hid financials, auto-relocks, and failed/
                successful authentication. For operational accountability &amp; compliance.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleAudit(!cfg.audit_enabled)}
            disabled={auditBusy}
            className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 disabled:opacity-60 ${cfg.audit_enabled ? "bg-[#10B981]" : "bg-muted"}`}
            data-testid="privacy-audit-toggle"
            aria-pressed={cfg.audit_enabled}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${cfg.audit_enabled ? "translate-x-6" : "translate-x-0.5"}`} />
          </button>
        </div>

        {cfg.audit_enabled && (
          <div className="space-y-2" data-testid="privacy-audit-log">
            <div className="flex items-center justify-between">
              <div className="label-mini text-muted-foreground">RECENT ACTIVITY</div>
              <button onClick={loadEvents} disabled={eventsLoading}
                className="text-xs px-2.5 py-1 border border-border hover:bg-secondary transition-colors disabled:opacity-60"
                data-testid="privacy-audit-refresh">
                {eventsLoading ? "Loading…" : "Refresh"}
              </button>
            </div>
            {events.length === 0 ? (
              <div className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-4 text-center">
                No events recorded yet.
              </div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border max-h-72 overflow-y-auto">
                {events.map((ev, i) => {
                  const a = AUDIT_LABELS[ev.action] || { label: ev.action, tone: "text-foreground" };
                  return (
                    <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid="privacy-audit-row">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`font-semibold ${a.tone}`}>{a.label}</span>
                        <span className="text-muted-foreground truncate">
                          {ev.user_name || ev.user_email || "—"}
                          {ev.meta?.method ? ` · ${ev.meta.method}` : ""}
                          {ev.action === "unlock_failed" && typeof ev.meta?.remaining_attempts === "number"
                            ? ` · ${ev.meta.remaining_attempts} left` : ""}
                        </span>
                      </div>
                      <span className="text-muted-foreground font-mono-tab whitespace-nowrap flex-shrink-0">
                        {fmtAuditTime(ev.at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="text-xs text-muted-foreground border-l-2 border-[#002FA7] bg-[#F0F4FF] p-3 rounded-r-lg">
        Revealed figures auto-hide again after <strong>5 minutes</strong> of inactivity. The same protection is
        reusable for Corporate Statements, Driver Earnings and future financial reports.
      </div>
    </div>
  );
};


const CycleConfigForm = ({ cycleConfig, onSave }) => {  const [form, setForm] = useState(null);
  useEffect(() => { setForm(cycleConfig); }, [cycleConfig]);
  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-4" data-testid="cycle-config-form">
      <div>
        <div className="label-mini text-muted-foreground">DRIVER CYCLE FEE</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Daily driver fee</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Recurring daily fee deducted from a driver's gross earnings (display only — no money is moved).
          Applied <span className="font-semibold">only on days the driver goes online</span>, resetting at UK midnight.
        </p>
      </div>

      <div className="flex items-center justify-between border border-border p-3">
        <div>
          <div className="label-mini">CYCLE FEE</div>
          <div className="text-xs text-muted-foreground mt-0.5">{form.enabled ? "Enabled — drivers see Net = Gross − Fee" : "Disabled — drivers see full gross"}</div>
        </div>
        <button
          type="button"
          onClick={() => set("enabled", !form.enabled)}
          className={`w-12 h-6 rounded-full transition-colors relative ${form.enabled ? "bg-[#10B981]" : "bg-muted"}`}
          data-testid="cycle-enabled-toggle"
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div>
        <Label className="label-mini">DAILY FEE AMOUNT (£)</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">Suggested £30/day to undercut competitor rates (~£45).</p>
        <Input
          type="number"
          step="0.50"
          min="0"
          value={form.fee_amount ?? 0}
          onChange={(e) => set("fee_amount", parseFloat(e.target.value))}
          className="font-mono-tab"
          data-testid="cycle-fee-input"
        />
      </div>

      <Button onClick={() => onSave(form)} className="w-full" data-testid="cycle-save-btn">
        <Settings className="w-4 h-4 mr-2" /> Save cycle fee
      </Button>

      <p className="text-xs text-muted-foreground border-t border-border pt-3">
        Note: Changes apply to <span className="font-semibold">future cycles only</span>. Today's open cycles keep the fee they were created with.
      </p>
    </div>
  );
};

const FareConfigForm = ({ fareConfig, onSave }) => {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(fareConfig); }, [fareConfig]);
  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (k, label, step = "0.1") => (
    <div>
      <Label className="label-mini">{label}</Label>
      <Input type="number" step={step} value={form[k] ?? ""} onChange={(e) => set(k, parseFloat(e.target.value))}
        className="mt-1.5 font-mono-tab" data-testid={`fare-${k}-input`} />
    </div>
  );
  const intInput = (k, label) => (
    <div>
      <Label className="label-mini">{label}</Label>
      <Input type="number" step="1" value={form[k] ?? ""} onChange={(e) => set(k, parseInt(e.target.value || "0", 10))}
        className="mt-1.5 font-mono-tab" data-testid={`fare-${k}-input`} />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="fare-config-form">
      <div>
        <div className="label-mini text-muted-foreground">FARE CONFIGURATION</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Pricing rules</h3>
        <p className="text-xs text-muted-foreground mt-1">Applied to all new ride estimates and completions.</p>
      </div>

      <div>
        <div className="label-mini text-muted-foreground mb-2">METER</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {num("base_fare", "BASE FARE (£)")}
          {num("minimum_fare", "MINIMUM (£)")}
          {num("per_km", "PER MILE (£)")}
          {num("per_minute", "PER MIN (£)")}
          {num("surge_multiplier", "MANUAL SURGE ×", "0.05")}
          <div>
            <Label className="label-mini">CURRENCY</Label>
            <Input value={form.currency || ""} onChange={(e) => set("currency", e.target.value)}
              className="mt-1.5 font-mono-tab" data-testid="fare-currency-input" />
          </div>
        </div>
      </div>

      <div>
        <div className="label-mini text-muted-foreground mb-2">ALPHA CARS EXTRAS</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {num("per_extra_stop", "EXTRA STOP (£)")}
          {num("dog_fee", "DOG FEE (£)")}
          {num("terminal_fee", "TERMINAL (£)")}
          {num("wait_return_min", "WAIT & RETURN MIN (£)")}
        </div>
      </div>

      <div>
        <div className="label-mini text-muted-foreground mb-2">LATE NIGHT (UK TIME)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {num("late_night_surcharge_rate", "SURCHARGE RATE (0.25 = 25%)", "0.05")}
          {intInput("late_night_start_hour", "START HOUR (0-23)")}
          {intInput("late_night_end_hour", "END HOUR (0-23)")}
        </div>
      </div>

      <div>
        <div className="label-mini text-muted-foreground mb-2">VAT</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {num("vat_rate", "VAT RATE (0.20 = 20%)", "0.01")}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Fares are inclusive of VAT. Receipts show <span className="font-semibold">Net + VAT = Gross</span>.
        </p>
      </div>

      <Button onClick={() => onSave(form)} className="w-full" data-testid="fare-save-btn">
        <Settings className="w-4 h-4 mr-2" /> Save fare rules
      </Button>
    </div>
  );
};

const SurgeConfigForm = ({ surgeConfig, onSave }) => {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(surgeConfig); }, [surgeConfig]);
  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4" data-testid="surge-config-form">
      <div>
        <div className="label-mini text-muted-foreground">DYNAMIC SURGE</div>
        <h3 className="font-heading text-xl font-bold tracking-tight">Auto-pricing engine</h3>
        <p className="text-xs text-muted-foreground mt-1">Fares scale up automatically when demand exceeds supply within 3 miles.</p>
      </div>

      <div className="flex items-center justify-between border border-border p-3">
        <div>
          <div className="label-mini">AUTO-SURGE</div>
          <div className="text-xs text-muted-foreground mt-0.5">{form.enabled ? "Active" : "Disabled"}</div>
        </div>
        <button
          type="button"
          onClick={() => set("enabled", !form.enabled)}
          className={`w-12 h-6 rounded-full transition-colors relative ${form.enabled ? "bg-[#10B981]" : "bg-muted"}`}
          data-testid="surge-enabled-toggle"
        >
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div>
        <Label className="label-mini">DEMAND : SUPPLY THRESHOLD</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">Surge kicks in above this ratio.</p>
        <Input type="number" step="0.1" value={form.threshold_ratio}
          onChange={(e) => set("threshold_ratio", parseFloat(e.target.value))}
          className="font-mono-tab" data-testid="surge-threshold-input" />
      </div>

      <div>
        <Label className="label-mini">MAX MULTIPLIER</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">Upper cap, e.g. 2.5× = 250% of base.</p>
        <Input type="number" step="0.1" value={form.max_multiplier}
          onChange={(e) => set("max_multiplier", parseFloat(e.target.value))}
          className="font-mono-tab" data-testid="surge-max-input" />
      </div>

      <Button onClick={() => onSave(form)} className="w-full" data-testid="surge-save-btn">
        <Zap className="w-4 h-4 mr-2" /> Save surge rules
      </Button>
    </div>
  );
};

const FixedRoutesPanel = ({ routes, onSave, onDelete }) => {
  const [editing, setEditing] = useState(null); // null | route | "new"
  return (
    <div className="space-y-4" data-testid="fixed-routes-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-mini text-muted-foreground">FIXED ROUTES</div>
          <h3 className="font-heading text-xl font-bold tracking-tight">Airport & long-distance flat fares</h3>
          <p className="text-xs text-muted-foreground mt-1">Bypasses the meter for trips where pickup or drop-off falls inside the radius.</p>
        </div>
        <Button onClick={() => setEditing({ name: "", lat: 51.4700, lng: -0.4543, radius_km: 5.0, price_car: 60, price_bus: 90 })} data-testid="add-route-btn">
          <Plus className="w-4 h-4 mr-1" /> Add route
        </Button>
      </div>

      <div className="space-y-2">
        {routes.map((r) => (
          <div key={r.id} className="border border-border p-3 flex items-center gap-3 transition-card" data-testid={`route-row-${r.id}`}>
            <Plane className="w-4 h-4 text-[#10B981] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{r.name}</div>
              <div className="label-mini text-muted-foreground font-mono-tab truncate">
                {r.lat.toFixed(4)}, {r.lng.toFixed(4)} · ±{r.radius_km}km
              </div>
            </div>
            <div className="text-right font-mono-tab text-sm flex-shrink-0">
              <div>Car £{r.price_car}</div>
              <div className="text-muted-foreground">Bus £{r.price_bus}</div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => setEditing(r)} className="text-xs px-2 py-1 border border-border hover:bg-secondary transition-colors" data-testid={`edit-route-${r.id}`}>
                Edit
              </button>
              <button onClick={() => onDelete(r.id, r.name)} className="text-xs px-2 py-1 border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors" data-testid={`delete-route-${r.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {routes.length === 0 && <div className="text-center py-12 text-sm text-muted-foreground">No fixed routes yet.</div>}
      </div>

      {editing && (
        <RouteEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={async (r) => {
            await onSave(r);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const RouteEditor = ({ initial, onCancel, onSave }) => {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const isNew = !form.id;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="route-editor-modal">
      <div className="bg-white border border-border p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-black">{isNew ? "New fixed route" : `Edit ${initial.name}`}</h3>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="route-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <Label className="label-mini">NAME</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="route-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-mini">LATITUDE</Label>
            <Input type="number" step="0.0001" value={form.lat} onChange={(e) => set("lat", parseFloat(e.target.value))} className="mt-1.5 font-mono-tab" data-testid="route-lat-input" />
          </div>
          <div>
            <Label className="label-mini">LONGITUDE</Label>
            <Input type="number" step="0.0001" value={form.lng} onChange={(e) => set("lng", parseFloat(e.target.value))} className="mt-1.5 font-mono-tab" data-testid="route-lng-input" />
          </div>
          <div>
            <Label className="label-mini">RADIUS (km)</Label>
            <Input type="number" step="0.5" value={form.radius_km} onChange={(e) => set("radius_km", parseFloat(e.target.value))} className="mt-1.5 font-mono-tab" data-testid="route-radius-input" />
          </div>
          <div />
          <div>
            <Label className="label-mini">CAR PRICE (£)</Label>
            <Input type="number" step="0.5" value={form.price_car} onChange={(e) => set("price_car", parseFloat(e.target.value))} className="mt-1.5 font-mono-tab" data-testid="route-price-car-input" />
          </div>
          <div>
            <Label className="label-mini">BUS PRICE (£)</Label>
            <Input type="number" step="0.5" value={form.price_bus} onChange={(e) => set("price_bus", parseFloat(e.target.value))} className="mt-1.5 font-mono-tab" data-testid="route-price-bus-input" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1" data-testid="route-cancel-btn">Cancel</Button>
          <Button onClick={() => onSave(form)} className="flex-1" disabled={!form.name} data-testid="route-save-btn">
            {isNew ? "Add route" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};
